/**
 * The board as tabs and cards, rather than columns.
 *
 * Columns were the right shape for a board people drag things across, and
 * the wrong one for this board: 114 rows sit at New and every other stage
 * is empty, so five sixths of the screen was blank headings and the one
 * column that had anything was a narrow strip of cut-off names. A tab per
 * stage gives the full width to whichever stage is being looked at, and
 * says what is in the others without drawing them.
 *
 * Dragging was how a card changed stage, and a tab cannot be dragged to.
 * Nothing replaces it on the card: a picker sitting in the footer of
 * sixteen cards is sixteen chances to move the wrong lead with one stray
 * click. Moving a lead is done inside it, where whoever is moving it can
 * see what they are moving.
 *
 * The card is the Deals page's card with a lead's facts in it — the two
 * pages show the same rows at different points, and they should not look
 * like two different products.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { parseDate, formatDate, daysAgo } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { Avatar, OwnerPill, Dash, EmptyState, SourceBadge } from './Primitives.jsx';
import PlanChip from './PlanChip.jsx';
import {
  isStalled, companyName, leadDetails, lastTouch, clientOfDeal, dealActivities
} from '../domain/crm.js';

export default function PipelineCards({ groups }) {
  const { S, crm, rows, setSheet, moveDealStage } = useApp();

  /* Open on the first stage that has anything in it. Landing on an empty
     New while every row waits at Qualified is a page that looks broken. */
  const first = (groups.find(g => g.deals.length) || groups[0] || {}).stage || '';
  const [tab, setTab] = useState(first);

  /*
   * Only fall back when the stage has gone, not when it is empty.
   *
   * This asked for a stage that still had rows in it, so choosing Qualified
   * with nothing at Qualified bounced straight back to New and the tabs
   * looked broken. An empty stage is a perfectly good thing to look at —
   * it is how somebody checks that nothing is sitting there.
   */
  useEffect(() => {
    if (!groups.some(g => g.stage === tab)) setTab(first);
  }, [first, groups, tab]);

  const group = groups.find(g => g.stage === tab) || groups[0];
  const list = group ? group.deals : [];

  const win = (e, d) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (window.confirm(`Mark “${d.title}” as won? It moves to Deals.`)) moveDealStage(d.id, 'Won');
  };

  return (
    <div className="pcwrap">
      <div className="pctabs" role="tablist" aria-label="Stage">
        {groups.map(g => (
          <button
            key={g.stage}
            type="button"
            role="tab"
            aria-selected={g.stage === tab}
            className={`pctab${g.stage === tab ? ' on' : ''}${g.deals.length ? '' : ' none'}`}
            onClick={() => setTab(g.stage)}
          >
            {g.stage}
            <em>{g.deals.length}</em>
          </button>
        ))}
      </div>

      {!list.length ? (
        <EmptyState icon="zap" tone="g-slate" title={`Nothing at ${tab}`}>
          Move a lead here from another stage, or clear the filters.
        </EmptyState>
      ) : (
        <div className="cards dgrid">
          {list.map(d => {
            const f = leadDetails(crm, d);
            const when = parseDate(f.requested);
            const touch = lastTouch(crm, d.id);
            const stale = isStalled(S, crm, d);
            const client = clientOfDeal(rows, d);
            const last = dealActivities(crm, d.id)[0] || null;

            return (
              <article
                className={`dcard2 lead${stale ? ' stale' : ''}`}
                key={d.id}
                role="button"
                tabIndex={0}
                onClick={() => setSheet({ kind: 'dealView', id: d.id })}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSheet({ kind: 'dealView', id: d.id });
                  }
                }}
              >
                <header>
                  <Avatar name={d.title} />
                  <div className="dc2who">
                    <b>{d.title}</b>
                    <small>
                      {[companyName(crm, d.cid), f.city, f.country].filter(Boolean).join(' · ')
                        || 'No company'}
                    </small>
                  </div>
                  <SourceBadge name={d.source} />
                </header>

                {/* How to reach them: the whole point of a lead, and what the
                    narrow column used to cut in half. */}
                <div className="pcreach">
                  {f.email ? (
                    <a
                      className="dealmail"
                      href={`mailto:${f.email}`}
                      title={f.email}
                      onClick={e => e.stopPropagation()}
                    >
                      <Icon name="mail" />{f.email}
                    </a>
                  ) : null}
                  {f.phone ? (
                    <a
                      className="dealmail"
                      href={`tel:${f.phone}`}
                      onClick={e => e.stopPropagation()}
                    >
                      <Icon name="phone" />{f.phone}
                    </a>
                  ) : null}
                  {!f.email && !f.phone ? <span className="dim">No address or number</span> : null}
                </div>

                {/* What was last done, in the place the Deals card keeps the
                    money: on the board this is the figure that matters. */}
                <div className="dc2money pclast">
                  <span className="dc2amt">
                    {last ? <><span className="lltype">{last.type}</span>{last.subject}</> : 'Nothing logged'}
                  </span>
                  <span className="dc2lab">
                    {last
                      ? `${formatDate(parseDate(last.due), false, S.dateFmt)}${last.who ? ` · ${last.who}` : ''}${last.done ? '' : ' · still open'}`
                      : 'no call, no note, nothing'}
                  </span>
                </div>

                <dl className="dc2facts">
                  <div>
                    <dt>Requested</dt>
                    <dd>{when ? formatDate(when, false, S.dateFmt) : <Dash />}</dd>
                  </div>
                  <div>
                    <dt>Landed on</dt>
                    <dd>
                      {f.landing ? (
                        <a
                          href={f.landing}
                          target="_blank"
                          rel="noreferrer"
                          title={f.landing}
                          onClick={e => e.stopPropagation()}
                        >
                          {f.landingPath}
                        </a>
                      ) : <Dash />}
                    </dd>
                  </div>
                </dl>

                <div className="dc2chips">
                  {client ? <span className="dealchip cli"><Icon name="check" />Client</span> : null}
                  {!d.origin ? <span className="dealchip man"><Icon name="edit" />Manual</span> : null}
                  {/* Which kind of form, and whether it went through.
                      "Demo request" used to be printed on every card here
                      whatever the row was, because it was the only kind the
                      sync brought across. Six kinds reach the board now, and
                      a WhatsApp click is a different conversation from a
                      submitted quote request. */}
                  {f.type ? <span className="dealchip kind">{f.type}</span> : null}
                  {f.state ? (
                    <span
                      className={`dealchip ${f.state === 'submitted' ? 'sub' : 'part'}`}
                      title={f.state === 'submitted'
                        ? 'The tracker saw this form go through'
                        : 'Typed but never confirmed — it may still have been sent'}
                    >
                      {f.state === 'submitted' ? 'Submitted' : 'Partial'}
                    </span>
                  ) : null}
                  {f.productList.length || f.products
                    ? <PlanChip list={f.productList} label={f.products} />
                    : null}
                  {d.cycle ? <span className="dealchip">{d.cycle}</span> : null}
                  {f.campaign ? <span className="dealchip" title="Campaign">{f.campaign}</span> : null}
                  {f.visits > 1
                    ? <span className="dealchip met" title="Visits to the site, this one included">
                        {f.visits} visits
                      </span>
                    : null}
                  {stale ? <span className="dealchip bad"><Icon name="alert" />Stale</span> : null}
                </div>

                <footer>
                  <OwnerPill name={d.owner} />
                  <span className="tsince">
                    {touch ? `${daysAgo(touch)}d ago` : 'never touched'}
                  </span>
                  <div className="acts" onClick={e => e.stopPropagation()}>
                    <button type="button" className="won" title="Mark as won — moves to Deals" onClick={e => win(e, d)}>
                      <Icon name="check" />
                    </button>
                    <button
                      type="button"
                      className="vw"
                      title="View details"
                      onClick={() => setSheet({ kind: 'dealView', id: d.id })}
                    >
                      <Icon name="eye" />
                    </button>
                    <button type="button" title="Edit" onClick={() => setSheet({ kind: 'deal', id: d.id })}>
                      <Icon name="edit" />
                    </button>
                  </div>
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
